/**
 * 预编译 i18n 加载器
 * 
 * 从预编译的 JSON 文件加载语言消息
 * 相比动态 import 82 个模块，性能提升约 10-50 倍
 * 
 * 使用前提：
 * 1. 运行 npm run build:i18n 生成预编译文件
 * 2. 确保 .output/i18n/*.json 文件存在
 * 
 * 路径说明：
 * - 预编译文件位置: .output/i18n/*.json
 * - Nuxt SSR 运行位置: .output/server/
 * - 因此需要 ../ 回到 .output 目录
 * 
 * 注意：此模块仅在服务端（SSR）使用，客户端会返回空实现
 * 注意：为避免循环依赖，此文件不导入 index.ts
 */

// 语言类型（避免从 index.ts 导入导致循环依赖）
type SupportedLanguage = string
type MessageSchema = Record<string, any>

// 缓存目录路径（避免重复计算）
let _precompiledDir: string = ''

// Node.js 模块（仅服务端）
let fsPromises: typeof import('fs/promises') | null = null
let fs: typeof import('fs') | null = null
let path: typeof import('path') | null = null

// 懒加载 Node.js 模块（避免客户端构建报错）
const loadNodeModules = async () => {
    if (typeof window !== 'undefined') return false
    if (fsPromises && fs && path) return true
    
    try {
        fsPromises = await import('fs/promises')
        fs = await import('fs')
        path = await import('path')
        return true
    } catch {
        return false
    }
}

/**
 * 获取预编译文件目录
 * 支持多种运行环境：
 * 1. Nuxt SSR 生产环境（.output/server/ 运行）
 * 2. 开发环境
 * 3. 直接 node 运行
 */
const getPrecompiledDir = (): string => {
    if (_precompiledDir) return _precompiledDir
    if (!path || !fs) return ''
    
    const { join } = path
    const { existsSync } = fs
    
    // 尝试多个可能的路径
    const possiblePaths: string[] = [
        // Nuxt 3 生产环境：从 .output/server/chunks/ 回到 .output/i18n/
        join(process.cwd(), '.output/i18n'),
        // 备用：当前工作目录
        join(process.cwd(), 'i18n'),
    ]
    
    for (const p of possiblePaths) {
        if (existsSync(join(p, 'en.json'))) {
            _precompiledDir = p
            console.log(`📁 [i18n] 预编译目录: ${p}`)
            return p
        }
    }
    
    // 默认返回第一个路径
    _precompiledDir = possiblePaths[0] || ''
    return _precompiledDir
}

// 缓存
const precompiledCache: Partial<Record<SupportedLanguage, MessageSchema>> = {}

// 模块加载状态
let _modulesLoaded = false
let _hasPrecompiled: boolean | null = null

/**
 * 初始化：加载 Node.js 模块并检测预编译文件
 * 仅在服务端执行
 */
const initPrecompiledLoader = async (): Promise<boolean> => {
    if (_modulesLoaded) return _hasPrecompiled ?? false
    
    const loaded = await loadNodeModules()
    _modulesLoaded = true
    
    if (!loaded) {
        _hasPrecompiled = false
        return false
    }
    
    const dir = getPrecompiledDir()
    if (dir && fs && path) {
        _hasPrecompiled = fs.existsSync(path.join(dir, 'en.json'))
    } else {
        _hasPrecompiled = false
    }
    
    return _hasPrecompiled
}

/**
 * 检查预编译文件是否存在
 * 注意：首次调用是异步的，后续调用返回缓存结果
 */
export const hasPrecompiledLocales = (): boolean => {
    // 客户端直接返回 false
    if (typeof window !== 'undefined') return false
    
    // 如果已检测过，返回缓存结果
    if (_hasPrecompiled !== null) return _hasPrecompiled
    
    // 同步检测（用于首次调用）
    // 注意：此时模块可能还未加载，返回 false，后续异步加载会更新
    return false
}

/**
 * 异步检查预编译文件是否可用
 */
export const checkPrecompiledAvailable = async (): Promise<boolean> => {
    return initPrecompiledLoader()
}

/**
 * 从预编译 JSON 加载语言消息
 * 比动态 import 快 10-50 倍
 */
export const loadPrecompiledLocale = async (
    lang: SupportedLanguage
): Promise<MessageSchema | null> => {
    // 客户端不支持
    if (typeof window !== 'undefined') return null
    
    // 已缓存
    if (precompiledCache[lang]) {
        return precompiledCache[lang]!
    }
    
    // 确保模块已加载
    const available = await initPrecompiledLoader()
    if (!available || !fsPromises || !fs || !path) {
        return null
    }
    
    const dir = getPrecompiledDir()
    if (!dir) return null
    
    const filePath = path.join(dir, `${lang}.json`)
    
    if (!fs.existsSync(filePath)) {
        console.warn(`⚠️ [i18n] 预编译文件不存在: ${filePath}`)
        return null
    }
    
    try {
        const startTime = Date.now()
        const content = await fsPromises.readFile(filePath, 'utf-8')
        const messages = JSON.parse(content)
        
        // 缓存
        precompiledCache[lang] = messages
        
        const elapsed = Date.now() - startTime
        console.log(`✅ [i18n] 从预编译加载 ${lang}: ${elapsed}ms`)
        
        return messages
    } catch (error) {
        console.error(`❌ [i18n] 加载预编译文件失败: ${filePath}`, error)
        return null
    }
}

/**
 * 清理预编译缓存（用于测试或内存紧张时）
 */
export const clearPrecompiledCache = (lang?: SupportedLanguage) => {
    if (lang) {
        delete precompiledCache[lang]
    } else {
        for (const key of Object.keys(precompiledCache)) {
            delete precompiledCache[key as SupportedLanguage]
        }
    }
}

/**
 * 获取预编译缓存状态
 */
export const getPrecompiledCacheStats = () => ({
    cached: Object.keys(precompiledCache),
    cacheSize: Object.keys(precompiledCache).length,
})
